import type { BinaryGrid } from "./symbolGrid";

export type SymbolGrammarMode = "off" | "light" | "rich";
export type SymbolShapeType = "mixed" | "circle" | "square";

export function applyStrokeGrammar(
    grid: BinaryGrid,
    rng: () => number,
    mode: SymbolGrammarMode,
    complexity: number,
    dotDensity: number,
    shapeFrequency: number,
    shapeType: SymbolShapeType,
): void {
    if (mode === "off") return;
    const passes = mode === "rich" ? 5 : 2;
    const baseLength = mode === "rich" ? 4 : 3;
    const dotChance = Math.max(0, Math.min(1, 0.15 + dotDensity * 0.75));
    const clampedShapeFrequency = Math.max(0, Math.min(1, shapeFrequency));
    for (let i = 0; i < passes; i += 1) {
        const anchor = pickActiveCell(grid, rng);
        if (!anchor) break;
        const [r, c] = anchor;
        const shapeRoll = rng();
        if (shapeRoll < 0.25) {
            addStem(grid, r, c, baseLength + Math.floor(complexity * 3), rng() < 0.5);
        } else if (shapeRoll < 0.5) {
            addBar(grid, r, c, baseLength + Math.floor(complexity * 2), rng() < 0.5);
        } else if (shapeRoll < 0.75) {
            addHook(grid, r, c, baseLength);
        } else {
            addLoop(grid, r, c, baseLength - 1);
            if (rng() < dotChance) addDotCluster(grid, r, c, rng, dotDensity);
        }
        if (rng() < dotChance * 0.35) {
            addDotCluster(grid, r, c, rng, dotDensity);
        }
    }

    const shapePasses = Math.round(clampedShapeFrequency * (mode === "rich" ? 10 : 5));
    for (let i = 0; i < shapePasses; i += 1) {
        const anchor = pickActiveCell(grid, rng) ?? pickAnyCell(grid, rng);
        if (!anchor) break;
        const [r, c] = anchor;
        const motifSize = 1 + Math.floor(complexity * 2 + rng() * 1.5);
        addShapeMotif(grid, r, c, rng, shapeType, motifSize);
    }
}

function pickActiveCell(grid: BinaryGrid, rng: () => number): [number, number] | null {
    const active: Array<[number, number]> = [];
    for (let r = 0; r < grid.length; r += 1) {
        for (let c = 0; c < (grid[0]?.length ?? 0); c += 1) {
            if (grid[r][c] > 0) active.push([r, c]);
        }
    }
    if (active.length === 0) return null;
    return active[Math.floor(rng() * active.length)] as [number, number];
}

function setCell(grid: BinaryGrid, r: number, c: number): void {
    if (r < 0 || r >= grid.length || c < 0 || c >= (grid[0]?.length ?? 0)) return;
    grid[r][c] = 1;
}

function clearCell(grid: BinaryGrid, r: number, c: number): void {
    if (r < 0 || r >= grid.length || c < 0 || c >= (grid[0]?.length ?? 0)) return;
    grid[r][c] = 0;
}

function pickAnyCell(grid: BinaryGrid, rng: () => number): [number, number] | null {
    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;
    if (!rows || !cols) return null;
    return [Math.floor(rng() * rows), Math.floor(rng() * cols)];
}

function addStem(grid: BinaryGrid, r: number, c: number, len: number, up: boolean): void {
    for (let i = 0; i < len; i += 1) {
        setCell(grid, up ? r - i : r + i, c);
    }
}

function addBar(grid: BinaryGrid, r: number, c: number, len: number, right: boolean): void {
    for (let i = 0; i < len; i += 1) {
        setCell(grid, r, right ? c + i : c - i);
    }
}

function addHook(grid: BinaryGrid, r: number, c: number, len: number): void {
    addStem(grid, r, c, len, true);
    addBar(grid, r - (len - 1), c, Math.max(2, len - 1), true);
}

function addLoop(grid: BinaryGrid, r: number, c: number, radius: number): void {
    const rr = Math.max(1, radius);
    for (let d = -rr; d <= rr; d += 1) {
        setCell(grid, r - rr, c + d);
        setCell(grid, r + rr, c + d);
        setCell(grid, r + d, c - rr);
        setCell(grid, r + d, c + rr);
    }
}

function addDot(grid: BinaryGrid, r: number, c: number, rng: () => number): void {
    const dr = rng() > 0.5 ? -2 : 2;
    const dc = rng() > 0.5 ? -1 : 1;
    setCell(grid, r + dr, c + dc);
}

function addDotCluster(
    grid: BinaryGrid,
    r: number,
    c: number,
    rng: () => number,
    dotDensity: number,
): void {
    const count = 1 + Math.floor(dotDensity * 4);
    for (let i = 0; i < count; i += 1) {
        const rr = r + Math.floor(rng() * 7) - 3;
        const cc = c + Math.floor(rng() * 7) - 3;
        addDot(grid, rr, cc, rng);
    }
}

function addShapeMotif(
    grid: BinaryGrid,
    r: number,
    c: number,
    rng: () => number,
    shapeType: SymbolShapeType,
    size: number,
): void {
    const chosen = shapeType === "mixed" ? (rng() < 0.5 ? "circle" : "square") : shapeType;
    if (chosen === "circle") {
        addCircleMotif(grid, r, c, size);
        return;
    }
    addSquareMotif(grid, r, c, size);
}

function addCircleMotif(grid: BinaryGrid, r: number, c: number, radius: number): void {
    const rr = Math.max(1, radius);
    for (let dr = -rr; dr <= rr; dr += 1) {
        for (let dc = -rr; dc <= rr; dc += 1) {
            const dist = Math.sqrt(dr * dr + dc * dc);
            if (dist >= rr - 0.45 && dist <= rr + 0.45) {
                setCell(grid, r + dr, c + dc);
            } else if (dist < rr - 0.8) {
                clearCell(grid, r + dr, c + dc);
            }
        }
    }
}

function addSquareMotif(grid: BinaryGrid, r: number, c: number, radius: number): void {
    const rr = Math.max(1, radius);
    for (let dr = -rr + 1; dr <= rr - 1; dr += 1) {
        for (let dc = -rr + 1; dc <= rr - 1; dc += 1) {
            clearCell(grid, r + dr, c + dc);
        }
    }
    for (let d = -rr; d <= rr; d += 1) {
        setCell(grid, r - rr, c + d);
        setCell(grid, r + rr, c + d);
        setCell(grid, r + d, c - rr);
        setCell(grid, r + d, c + rr);
    }
}
